<template>
  <div class="mx-4 mt-4">
    <!-- Stats Cards -->
    <div class="row mb-4">
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('POS_PRODUCTS_REPORT.TOTAL_QUANTITY')"
          type="gradient-primary"
          :sub-title="formatNumber(stats.totalQte)"
          icon="fas fa-boxes"
        >
        </stats-card>
      </div>
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('POS_PRODUCTS_REPORT.TOTAL_NET_SALES')"
          type="gradient-green"
          :sub-title="formatCurrency(stats.totalPrix)"
          icon="fas fa-cash-register"
        >
        </stats-card>
      </div>
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('POS_PRODUCTS_REPORT.TOTAL_DISCOUNT')"
          type="gradient-orange"
          :sub-title="formatCurrency(stats.totalEscompte)"
          icon="fas fa-percent"
        >
        </stats-card>
      </div>
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('POS_PRODUCTS_REPORT.TOTAL_GROUPS')"
          type="gradient-info"
          :sub-title="formatNumber(stats.totalGroups)"
          icon="fas fa-list"
        >
        </stats-card>
      </div>
    </div>

    <div class="card col-12">
      <div class="card-header border-0">
        <div class="row align-items-center">
          <div class="col">
            <h3 class="mb-0">{{ $t("POS_PRODUCTS_REPORT.TITLE") }}</h3>
            <small class="text-muted">{{ $t("POS_PRODUCTS_REPORT.SUBTITLE") }}</small>
          </div>
        </div>
      </div>

      <div class="card-body">
        <!-- Filters -->
        <div class="row mb-4">
          <div
            class="col-md-3"
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
          >
            <base-input :label="$t('COMMON.ORGANIZATION')">
              <organization-selector
                :organization="filters.organization_id"
                :filterable="true"
                :showAll="false"
                @organizationChanged="
                  (organizationId) => {
                    filters.organization_id = organizationId;
                  }
                "
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.DATE_RANGE')">
              <flat-picker
                v-model="dateRange"
                :config="dateRangeConfig"
                class="form-control"
                :placeholder="$t('COMMON.DATES_PLACEHOLDER')"
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('POS_PRODUCTS_REPORT.SERVICE_CENTER')">
              <el-select
                v-model="filters.service_center_id"
                clearable
                filterable
                :placeholder="$t('COMMON.ALL')"
                class="select-primary"
              >
                <el-option
                  v-for="sc in serviceCenterOptions"
                  :key="sc.id"
                  :label="sc.name"
                  :value="sc.id"
                />
              </el-select>
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('POS_PRODUCTS_REPORT.ESTABLISHMENTS')">
              <el-select
                v-model="filters.establishment_ids"
                multiple
                filterable
                collapse-tags
                :placeholder="$t('COMMON.ALL')"
                class="select-primary"
              >
                <el-option
                  v-for="e in establishmentOptions"
                  :key="e.id"
                  :label="
                    e.active === false
                      ? `${e.name} (${$t('ESTABLISHMENTS.INACTIVE')})`
                      : e.name
                  "
                  :value="e.id"
                >
                  <span>{{ e.name }}</span>
                  <el-tag
                    v-if="e.active === false"
                    size="mini"
                    type="danger"
                    class="ml-2"
                  >
                    {{ $t("ESTABLISHMENTS.INACTIVE") }}
                  </el-tag>
                </el-option>
              </el-select>
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('POS_PRODUCTS_REPORT.CAFETERIA')">
              <el-select
                v-model="filters.cafeteria_id"
                clearable
                filterable
                :placeholder="$t('COMMON.ALL')"
                class="select-primary"
              >
                <el-option
                  v-for="c in availableCafeterias"
                  :key="c.id"
                  :label="c.name"
                  :value="c.id"
                />
              </el-select>
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('POS_PRODUCTS_REPORT.CAISSES')">
              <el-select
                v-model="filters.cash_register_ids"
                multiple
                collapse-tags
                :placeholder="$t('COMMON.ALL')"
                class="select-primary"
              >
                <el-option
                  v-for="c in availableCaisses"
                  :key="c.value"
                  :label="c.label"
                  :value="c.value"
                />
              </el-select>
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input
              :label="$t('POS_PRODUCTS_REPORT.SEARCH')"
              v-model="filters.search"
            />
          </div>
          <div class="col-md-6">
            <base-input :label="$t('POS_PRODUCTS_REPORT.GROUP_BY')">
              <el-select
                v-model="filters.group_by"
                multiple
                :placeholder="$t('POS_PRODUCTS_REPORT.DIMENSION_PRODUCT')"
                class="select-primary"
              >
                <el-option
                  v-for="option in groupByOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row mb-4">
          <div class="col">
            <base-button type="primary" @click="applyFilters">
              {{ $t("COMMON.APPLY_FILTERS") }}
            </base-button>
            <base-button type="secondary" @click="resetFilters">
              {{ $t("COMMON.RESET") }}
            </base-button>
            <base-button
              type="success"
              @click="exportToExcel"
              :loading="exportLoading"
              :disabled="exportLoading"
            >
              <i class="fas fa-file-excel mr-1"></i>
              {{ $t("COMMON.EXPORT_EXCEL") }}
            </base-button>
          </div>
        </div>

        <!-- Results Table -->
        <el-table
          class="align-items-center table-flush"
          header-row-class-name="thead-light"
          :data="reportData"
          v-loading="loading"
          row-key="id"
          show-summary
          :summary-method="getSummaries"
        >
          <div slot="empty" v-if="loading">
            <img src="/img/loading.gif" style="height: 100px; width: 100px" />
          </div>

          <!-- Dynamic dimension columns -->
          <el-table-column
            v-for="col in dimensionColumns"
            :key="col.prop"
            :label="col.label"
            :prop="col.prop"
            :min-width="col.minWidth"
          >
            <template v-slot="{ row }">
              {{ formatDim(row, col) }}
            </template>
          </el-table-column>

          <!-- Metric columns -->
          <el-table-column
            :label="$t('POS_PRODUCTS_REPORT.QUANTITY')"
            prop="qte"
            min-width="110px"
            align="right"
          >
            <template v-slot="{ row }">{{ formatNumber(row.qte) }}</template>
          </el-table-column>
          <el-table-column
            :label="$t('POS_PRODUCTS_REPORT.NET_SALES')"
            prop="prix"
            min-width="130px"
            align="right"
          >
            <template v-slot="{ row }">{{ formatCurrency(row.prix) }}</template>
          </el-table-column>
          <el-table-column
            :label="$t('POS_PRODUCTS_REPORT.DISCOUNT')"
            prop="escompte"
            min-width="120px"
            align="right"
          >
            <template v-slot="{ row }">{{ formatCurrency(row.escompte) }}</template>
          </el-table-column>
          <el-table-column
            :label="$t('POS_PRODUCTS_REPORT.COST')"
            prop="cout"
            min-width="110px"
            align="right"
          >
            <template v-slot="{ row }">{{ formatCurrency(row.cout) }}</template>
          </el-table-column>
        </el-table>

        <small class="text-muted d-block mt-2">
          <i class="fas fa-info-circle mr-1"></i>{{ $t("POS_PRODUCTS_REPORT.COST_UNAVAILABLE") }}
        </small>

        <!-- Pagination -->
        <div
          class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
        >
          <div class="">
            <p class="card-category">
              {{
                $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
                  from: total ? from + 1 : 0,
                  to: to,
                  of: total,
                })
              }}
            </p>
          </div>
          <base-pagination
            class="pagination-no-border"
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import * as XLSX from "xlsx";
import { Table, TableColumn, Select, Option, Tag } from "element-ui";
import { BasePagination, StatsCard } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import service from "@/store/services/pos-products-report-service";

// Default to the last 30 days: POS data may not yet cover the current month.
function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return [fmt(start), fmt(end)];
}

export default {
  name: "PosProductsReportPage",

  components: {
    flatPicker,
    BasePagination,
    StatsCard,
    OrganizationSelector,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Tag.name]: Tag,
  },

  data() {
    const [firstDay, lastDay] = defaultDateRange();

    return {
      dateRange: [firstDay, lastDay],
      exportLoading: false,
      appliedGroupBy: ["product"],
      filters: {
        organization_id: null,
        service_center_id: null,
        establishment_ids: [],
        cash_register_ids: [],
        cafeteria_id: null,
        search: null,
        group_by: ["product"],
      },
      filterOptions: {
        establishments: [],
        service_centers: [],
      },
      pagination: {
        perPage: 25,
        currentPage: 1,
      },
      dateRangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        allowInput: true,
        defaultDate: [firstDay, lastDay],
        locale: this.$flatPickrLocale ? this.$flatPickrLocale() : undefined,
      },
    };
  },

  created() {
    this.groupByOptions = [
      { value: "establishment", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_ESTABLISHMENT") },
      { value: "service_center", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_SERVICE_CENTER") },
      { value: "cafeteria", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_CAFETERIA") },
      { value: "caisse", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_CAISSE") },
      { value: "product", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_PRODUCT") },
      { value: "department", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_DEPARTMENT") },
      { value: "day", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_DAY") },
      { value: "week", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_WEEK") },
      { value: "month", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_MONTH") },
      { value: "cashier", label: this.$t("POS_PRODUCTS_REPORT.DIMENSION_CASHIER") },
    ];
  },

  computed: {
    ...mapGetters({
      reportData: "posProductsReport/reportData",
      meta: "posProductsReport/meta",
      loading: "posProductsReport/loading",
    }),

    stats() {
      return (
        this.meta?.stats || {
          totalGroups: 0,
          totalQte: 0,
          totalCout: 0,
          totalPrix: 0,
          totalEscompte: 0,
        }
      );
    },

    serviceCenterOptions() {
      return [...(this.filterOptions.service_centers || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "fr")
      );
    },

    establishmentOptions() {
      const all = this.filterOptions.establishments || [];
      if (!this.filters.service_center_id) {
        return all;
      }
      return all.filter((e) => e.service_center_id === this.filters.service_center_id);
    },

    selectedEstablishments() {
      const all = this.establishmentOptions;
      if (!this.filters.establishment_ids.length) {
        return all;
      }
      return all.filter((e) => this.filters.establishment_ids.includes(e.id));
    },

    availableCaisses() {
      const list = [];
      this.selectedEstablishments.forEach((e) => {
        (e.caisses || []).forEach((c) => {
          list.push({
            value: c.cash_register_id,
            label: `${c.name}`,
          });
        });
      });
      return list;
    },

    availableCafeterias() {
      const map = {};
      this.selectedEstablishments.forEach((e) => {
        (e.cafeterias || []).forEach((c) => {
          map[c.id] = c;
        });
      });
      return Object.values(map);
    },

    dimensionColumns() {
      return this.columnsFor(this.appliedGroupBy);
    },

    total() {
      return this.meta?.page?.total || 0;
    },

    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    "pagination.currentPage": {
      handler: "fetchData",
      immediate: false,
    },
    "filters.service_center_id": {
      handler() {
        const validEst = new Set(this.establishmentOptions.map((e) => e.id));
        this.filters.establishment_ids = this.filters.establishment_ids.filter((id) =>
          validEst.has(id)
        );
        this.pruneDependentFilters();
      },
    },
    "filters.establishment_ids": {
      handler: "pruneDependentFilters",
    },
  },

  methods: {
    ...mapActions({
      fetchReport: "posProductsReport/fetchReport",
    }),

    pruneDependentFilters() {
      const validCr = new Set(this.availableCaisses.map((c) => c.value));
      this.filters.cash_register_ids = this.filters.cash_register_ids.filter((id) =>
        validCr.has(id)
      );
      const validCaf = new Set(this.availableCafeterias.map((c) => c.id));
      if (this.filters.cafeteria_id && !validCaf.has(this.filters.cafeteria_id)) {
        this.filters.cafeteria_id = null;
      }
    },

    columnsFor(groupBy) {
      const cols = [];
      (groupBy || []).forEach((dim) => {
        switch (dim) {
          case "establishment":
            cols.push({ prop: "establishment_name", label: this.$t("POS_PRODUCTS_REPORT.ESTABLISHMENT"), minWidth: "200px" });
            break;
          case "service_center":
            cols.push({ prop: "service_center_name", label: this.$t("POS_PRODUCTS_REPORT.SERVICE_CENTER"), minWidth: "200px" });
            break;
          case "cafeteria":
            cols.push({ prop: "cafeteria_name", label: this.$t("POS_PRODUCTS_REPORT.CAFETERIA"), minWidth: "180px" });
            break;
          case "caisse":
            cols.push({ prop: "caisse_name", label: this.$t("POS_PRODUCTS_REPORT.CAISSE"), minWidth: "150px" });
            break;
          case "product":
            cols.push({ prop: "noarticle", label: this.$t("POS_PRODUCTS_REPORT.ARTICLE"), minWidth: "120px" });
            cols.push({ prop: "description", label: this.$t("POS_PRODUCTS_REPORT.DESCRIPTION"), minWidth: "220px" });
            break;
          case "department":
            cols.push({ prop: "dept_name", label: this.$t("POS_PRODUCTS_REPORT.DEPARTMENT"), minWidth: "180px", kind: "department" });
            break;
          case "day":
          case "week":
          case "month":
            cols.push({ prop: "period", label: this.$t("POS_PRODUCTS_REPORT.PERIOD"), minWidth: "130px" });
            break;
          case "cashier":
            cols.push({ prop: "cashier_name", label: this.$t("POS_PRODUCTS_REPORT.CASHIER"), minWidth: "150px", kind: "cashier" });
            break;
        }
      });
      return cols;
    },

    async loadFilterOptions() {
      try {
        const { data } = await service.getFilters({
          organization_id: this.filters.organization_id || undefined,
        });
        this.filterOptions = {
          establishments: data.establishments || [],
          service_centers: data.service_centers || [],
        };
      } catch (e) {
        // Non-blocking: if filter options fail to load, selectors stay empty.
      }
    },

    getDateParams() {
      let startDate = undefined;
      let endDate = undefined;

      if (this.dateRange) {
        if (Array.isArray(this.dateRange)) {
          startDate = this.dateRange[0];
          endDate = this.dateRange[1] || this.dateRange[0];
        } else {
          let dates = this.dateRange.split(" to ");
          if (dates.length === 1) {
            dates = this.dateRange.split(" au ");
          }
          if (dates.length === 2) {
            startDate = dates[0].trim();
            endDate = dates[1].trim();
          } else if (dates.length === 1 && dates[0]) {
            startDate = dates[0].trim();
            endDate = dates[0].trim();
          }
        }
      }
      return { startDate, endDate };
    },

    buildParams() {
      const { startDate, endDate } = this.getDateParams();

      const groupBy =
        this.filters.group_by.length > 0 ? this.filters.group_by : ["product"];

      return {
        organization_id: this.filters.organization_id || undefined,
        service_center_id: this.filters.service_center_id || undefined,
        establishment_ids:
          this.filters.establishment_ids.length > 0 ? this.filters.establishment_ids : undefined,
        cash_register_ids:
          this.filters.cash_register_ids.length > 0 ? this.filters.cash_register_ids : undefined,
        cafeteria_id: this.filters.cafeteria_id || undefined,
        search: this.filters.search || undefined,
        group_by: groupBy,
        start_date: startDate,
        end_date: endDate,
      };
    },

    async fetchData() {
      const params = {
        ...this.buildParams(),
        page: this.pagination.currentPage,
        per_page: this.pagination.perPage,
      };

      const { meta } = await this.fetchReport(params);
      this.appliedGroupBy = meta?.group_by || params.group_by;
    },

    applyFilters() {
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    resetFilters() {
      const [firstDay, lastDay] = defaultDateRange();
      this.dateRange = [firstDay, lastDay];
      this.filters = {
        organization_id: null,
        service_center_id: null,
        establishment_ids: [],
        cash_register_ids: [],
        cafeteria_id: null,
        search: null,
        group_by: ["product"],
      };
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    formatDim(row, col) {
      if (col.kind === "department") {
        const code = row.dept ? `${row.dept} - ` : "";
        return `${code}${row.dept_name || ""}` || "-";
      }
      if (col.kind === "cashier") {
        return row.cashier_name || (row.cashier != null ? `#${row.cashier}` : "-");
      }
      const v = row[col.prop];
      return v === null || v === undefined || v === "" ? "-" : v;
    },

    getSummaries(param) {
      const { columns } = param;
      const sums = [];
      const statsMap = {
        qte: { value: this.stats.totalQte, format: "number" },
        prix: { value: this.stats.totalPrix, format: "currency" },
        escompte: { value: this.stats.totalEscompte, format: "currency" },
        cout: { value: this.stats.totalCout, format: "currency" },
      };

      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = this.$t("POS_PRODUCTS_REPORT.TOTAL");
          return;
        }
        const prop = column.property;
        if (statsMap[prop]) {
          const { value, format } = statsMap[prop];
          sums[index] = format === "currency" ? this.formatCurrency(value) : this.formatNumber(value);
        } else {
          sums[index] = "";
        }
      });

      return sums;
    },

    async exportToExcel() {
      this.exportLoading = true;
      try {
        const params = this.buildParams();
        const { data, meta } = await service.getAllReport(params);

        const dimCols = this.columnsFor(meta?.group_by || params.group_by);
        const headers = [
          ...dimCols.map((c) => c.label),
          this.$t("POS_PRODUCTS_REPORT.QUANTITY"),
          this.$t("POS_PRODUCTS_REPORT.NET_SALES"),
          this.$t("POS_PRODUCTS_REPORT.DISCOUNT"),
          this.$t("POS_PRODUCTS_REPORT.COST"),
        ];

        const rows = data.map((row) => {
          const r = dimCols.map((c) => this.formatDim(row, c));
          r.push(row.qte || 0, row.prix || 0, row.escompte || 0, row.cout || 0);
          return r;
        });

        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, this.$t("POS_PRODUCTS_REPORT.TITLE").slice(0, 31));

        const { startDate, endDate } = this.getDateParams();
        const dateStr = startDate && endDate ? `${startDate}_${endDate}` : new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `pos_products_report_${dateStr}.xlsx`);
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.exportLoading = false;
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("fr-CA", {
        style: "currency",
        currency: "CAD",
      }).format(amount || 0);
    },

    formatNumber(value) {
      return new Intl.NumberFormat("fr-CA").format(value || 0);
    },
  },

  mounted() {
    this.loadFilterOptions();
    this.fetchData();
  },
};
</script>
